import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Row,
  Col,
  Card,
  Statistic,
  Button,
  Spin,
  Typography,
  Space,
} from 'antd';
import {
  TeamOutlined,
  BookOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  PlusOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { api } from '@/services/api';
import { API_ENDPOINTS } from '@/constants';
import type { ApiResponse, TeacherOverview } from '@/types';

const { Title, Text } = Typography;

const STAT_CARDS: Array<{
  key: 'totalStudents' | 'totalDecks' | 'publishedDecks' | 'totalSessions';
  title: string;
  icon: React.ReactNode;
  color: string;
  bg: string;
  route: string;
  state?: Record<string, unknown>;
}> = [
  {
    key: 'totalStudents',
    title: 'Total Students',
    icon: <TeamOutlined />,
    color: '#6366f1',
    bg: '#eef2ff',
    route: '/teacher/students',
  },
  {
    key: 'totalDecks',
    title: 'Total Decks',
    icon: <BookOutlined />,
    color: '#0891b2',
    bg: '#ecfeff',
    route: '/teacher/decks',
  },
  {
    key: 'publishedDecks',
    title: 'Published Decks',
    icon: <ThunderboltOutlined />,
    color: '#16a34a',
    bg: '#f0fdf4',
    route: '/teacher/decks',
    state: { statusFilter: 'PUBLISHED' },
  },
  {
    key: 'totalSessions',
    title: 'Total Sessions',
    icon: <BarChartOutlined />,
    color: '#ea580c',
    bg: '#fff7ed',
    route: '#',
  },
];

export default function DashboardPage() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['teacher', 'overview'],
    queryFn: () =>
      api.get<never, ApiResponse<TeacherOverview>>(API_ENDPOINTS.ANALYTICS_OVERVIEW),
  });

  const overview = data?.data;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          Dashboard
        </Title>
        <Text type="secondary">Welcome back! Here is an overview of your teaching activity.</Text>
      </div>

      <Row gutter={[16, 16]}>
        {STAT_CARDS.map((stat) => (
          <Col xs={24} sm={12} lg={6} key={stat.key}>
            <Card
              hoverable
              style={{ borderRadius: 12, cursor: 'pointer' }}
              onClick={() => navigate(stat.route, { state: stat.state })}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: stat.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                    color: stat.color,
                    flexShrink: 0,
                  }}
                >
                  {stat.icon}
                </div>
                <Statistic
                  title={stat.title}
                  value={overview?.[stat.key] ?? 0}
                  valueStyle={{ fontSize: 28, fontWeight: 700, color: stat.color }}
                />
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ marginTop: 24, borderRadius: 12 }}>
        <Title level={5} style={{ marginBottom: 16 }}>
          Quick Actions
        </Title>
        <Space size="middle" wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => navigate('/teacher/decks/new')}
          >
            Create Deck
          </Button>
          <Button
            icon={<UserAddOutlined />}
            size="large"
            onClick={() => navigate('/teacher/students')}
          >
            Invite Student
          </Button>
        </Space>
      </Card>
    </div>
  );
}
